/**
 * Compact date ranges: "Sep 7 – 13, 2026" rather than
 * "Sep 7, 2026 - Sep 13, 2026" (#277).
 *
 * Why a hand-written moment table rather than Intl: Hermes, the engine the
 * mobile app ships, has no Intl range formatter, and react-intl's date-time
 * range helper depends on it. Jest runs on Node, whose ICU does
 * implement it, so code using it would pass every test and throw on a phone.
 * `__tests__/utils/Dates/noFormatRange.test.ts` keeps it out of core and
 * mobile.
 *
 * The token order of each entry was drafted from Node's ICU (CLDR) range
 * patterns and then hand-reviewed against the app. Where CLDR picks a numeric
 * style (ja, cs, sk, bg), the entry keeps the month-name style the locale's
 * `ll` already renders elsewhere in the app. Separator spacing follows CLDR:
 * a bare en dash inside a month for most locales, thin spaces around it for
 * most Latin-script locales, and ordinary spaces where CLDR uses them.
 *
 * This module imports moment and the leaf `clock.ts` only, never the utils
 * barrel (see `importCycles.test.ts`).
 */
import moment from 'moment-timezone';
import {clockFormat} from './clock';
import type {DateTimeConfig} from './index';

export type RangeStyle = 'visual' | 'spoken';

export type RangeFormatOptions = {
  /**
   * `visual` (default) abbreviates months for the screen; `spoken` spells
   * them out for a screen-reader label.
   */
  style?: RangeStyle;
  /**
   * Joins the two ends in the spoken style, e.g. with a translated
   * "{start} to {end}". Supplied by the caller so core stays free of intl.
   * Without it, the spoken ends are joined with the visual separator.
   */
  joinSpokenRange?: (start: string, end: string) => string;
};

export type RangeWithTimeOptions = RangeFormatOptions & {
  /**
   * The user's date and time preferences: the times go on the user's clock.
   * Without them, on the moment locale's own (`LT`), 12-hour in English
   * (FE #438).
   */
  dateTimeConfig?: DateTimeConfig;
};

/** `[start format, end format, separator]`, in moment tokens. */
export type RangeCase = readonly [
  start: string,
  end: string,
  separator: string,
];

export type RangeCases = {
  /** Both ends in the same month: "Sep 7 – 13, 2026". */
  sameMonth: RangeCase;
  /** Same year, two months: "Aug 31 – Sep 6, 2026". */
  sameYear: RangeCase;
  /** Two years: "Dec 28, 2026 – Jan 3, 2027". */
  crossYear: RangeCase;
};

export type LocaleRangeFormats = RangeCases & {
  /**
   * Spoken formats, when the mechanical derivation (`MMM` → `MMMM`,
   * `ll` → `LL`) would not read like the locale's own `LL`.
   */
  spoken?: RangeCases;
};

const EN_DASH = '\u2013';
const THIN_SPACE = '\u2009';

/** "7–13" */
const TIGHT = EN_DASH;
/** "Aug 31 – Sep 6", with U+2009 THIN SPACE on both sides. */
const THIN = `${THIN_SPACE}${EN_DASH}${THIN_SPACE}`;
/** "31 авг. – 6 сент.", with ordinary spaces. */
const SPACED = ` ${EN_DASH} `;
/** U+FF5E, CLDR's ja range separator. */
const FULLWIDTH_TILDE = '\uff5e';

const llRange = (separator: string): RangeCase => ['ll', 'll', separator];

/**
 * Keyed by moment locale name: the app's `sr` is moment's `sr-cyrl`
 * (`Dates.setLocale`). `RANGE_FORMATS` must cover every app locale; a test
 * compares it with `packages/i18n/src/translations`.
 */
export const RANGE_FORMATS: Record<string, LocaleRangeFormats> = {
  ar: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', SPACED],
    crossYear: llRange(SPACED),
  },
  bg: {
    sameMonth: ['D', 'D MMM YYYY', SPACED],
    sameYear: ['D MMM', 'D MMM YYYY', SPACED],
    crossYear: llRange(SPACED),
  },
  ca: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
    // ca's LL is "D MMMM [de] YYYY": the long month already carries its own
    // "de"/"d'", but the year needs one too.
    spoken: {
      sameMonth: ['D', 'D MMMM [de] YYYY', TIGHT],
      sameYear: ['D MMMM', 'D MMMM [de] YYYY', THIN],
      crossYear: ['LL', 'LL', THIN],
    },
  },
  cs: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  da: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  de: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  el: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', SPACED],
    crossYear: llRange(SPACED),
  },
  en: {
    sameMonth: ['MMM D', 'D, YYYY', THIN],
    sameYear: ['MMM D', 'MMM D, YYYY', THIN],
    crossYear: llRange(THIN),
  },
  // English with the day-first date order (FE #430): CLDR en-GB.
  'en-dmy': {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  es: {
    sameMonth: ['D', 'D [de] MMM [de] YYYY', TIGHT],
    sameYear: ['D [de] MMM', 'D [de] MMM [de] YYYY', THIN],
    crossYear: llRange(THIN),
  },
  fr: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  hr: {
    sameMonth: ['Do', 'Do MMM YYYY', THIN],
    sameYear: ['Do MMM', 'Do MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  hu: {
    sameMonth: ['YYYY. MMM D', 'D.', TIGHT],
    sameYear: ['YYYY. MMM D.', 'MMM D.', THIN],
    crossYear: llRange(THIN),
  },
  it: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  ja: {
    // ja months have no short/long split, so the spoken style is the same.
    sameMonth: ['YYYY[年]M[月]D[日]', 'D[日]', FULLWIDTH_TILDE],
    sameYear: ['YYYY[年]M[月]D[日]', 'M[月]D[日]', FULLWIDTH_TILDE],
    crossYear: llRange(FULLWIDTH_TILDE),
  },
  ko: {
    sameMonth: ['YYYY[년] MMMM D[일]', 'D[일]', '~'],
    sameYear: ['YYYY[년] MMMM D[일]', 'MMMM D[일]', ' ~ '],
    crossYear: llRange(' ~ '),
  },
  nb: {
    sameMonth: ['D.', 'D. MMM YYYY', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  nl: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  pl: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  pt: {
    sameMonth: ['D', 'D [de] MMM [de] YYYY', THIN],
    sameYear: ['D [de] MMM', 'D [de] MMM [de] YYYY', THIN],
    crossYear: llRange(THIN),
  },
  ru: {
    sameMonth: ['D', 'D MMM YYYY [г.]', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY [г.]', SPACED],
    crossYear: llRange(SPACED),
  },
  sk: {
    sameMonth: ['D.', 'D. MMM YYYY', THIN],
    sameYear: ['D. MMM', 'D. MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  'sr-cyrl': {
    sameMonth: ['D.', 'D. MMM YYYY.', TIGHT],
    sameYear: ['D. MMM', 'D. MMM YYYY.', SPACED],
    crossYear: llRange(SPACED),
  },
  sv: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', TIGHT],
    crossYear: llRange(TIGHT),
  },
  tr: {
    sameMonth: ['D', 'D MMM YYYY', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY', THIN],
    crossYear: llRange(THIN),
  },
  uk: {
    sameMonth: ['D', 'D MMM YYYY [р.]', TIGHT],
    sameYear: ['D MMM', 'D MMM YYYY [р.]', SPACED],
    crossYear: llRange(SPACED),
  },
};

/** For a moment locale with no entry: "ll – ll". */
const FALLBACK: LocaleRangeFormats = {
  sameMonth: llRange(SPACED),
  sameYear: llRange(SPACED),
  crossYear: llRange(SPACED),
};

/** `MMM` → `MMMM` and `ll` → `LL`, leaving `[literal]` text alone. */
const toSpokenFormat = (format: string) =>
  format.replace(/\[[^\]]*\]|MMMM|MMM|ll/g, (token) => {
    if (token === 'MMM') {
      return 'MMMM';
    }
    if (token === 'll') {
      return 'LL';
    }
    return token;
  });

const toSpokenCase = ([start, end, separator]: RangeCase): RangeCase => [
  toSpokenFormat(start),
  toSpokenFormat(end),
  separator,
];

const spokenCases = (formats: LocaleRangeFormats): RangeCases =>
  formats.spoken ?? {
    sameMonth: toSpokenCase(formats.sameMonth),
    sameYear: toSpokenCase(formats.sameYear),
    crossYear: toSpokenCase(formats.crossYear),
  };

/**
 * Formats `[start, end]` (unix seconds) as a compact range in `timezone`, in
 * the active moment locale.
 *
 * Built for calendar periods that span at least two days, with
 * `start < end` (a week today). A single-day range would print "Sep 7 – 7";
 * callers with such ranges should format a single date instead.
 */
export const formatDateRange = (
  start: number,
  end: number,
  timezone: string,
  {style = 'visual', joinSpokenRange}: RangeFormatOptions = {},
) => {
  const from = moment.tz(start * 1000, timezone);
  const to = moment.tz(end * 1000, timezone);
  const formats = RANGE_FORMATS[from.locale()] ?? FALLBACK;
  const cases = style === 'spoken' ? spokenCases(formats) : formats;

  let rangeCase = cases.sameMonth;
  if (from.year() !== to.year()) {
    rangeCase = cases.crossYear;
  } else if (from.month() !== to.month()) {
    rangeCase = cases.sameYear;
  }
  const [startFormat, endFormat, separator] = rangeCase;
  const startText = from.format(startFormat);
  const endText = to.format(endFormat);

  if (style === 'spoken' && joinSpokenRange) {
    return joinSpokenRange(startText, endText);
  }
  return `${startText}${separator}${endText}`;
};

/**
 * What the locale puts between a date and a time, as a moment format
 * fragment: `lll` minus its leading `ll` and trailing `LT` (`LLL`, `LL` for
 * the spoken style). " " in most locales, ", " in ca/ru/uk, " [kl.] " in nb;
 * spoken, sv adds " [kl.] " and ca " [a les] ".
 * Falls back to a space for a locale whose `lll` is not built that way;
 * every app locale is, and a test holds them to it.
 */
const dateTimeJoiner = (locale: string, style: RangeStyle = 'visual') => {
  const data = moment.localeData(locale);
  const spoken = style === 'spoken';
  const date = data.longDateFormat(spoken ? 'LL' : 'll');
  const dateTime = data.longDateFormat(spoken ? 'LLL' : 'lll');
  const time = data.longDateFormat('LT');
  if (
    dateTime.length > date.length + time.length &&
    dateTime.startsWith(date) &&
    dateTime.endsWith(time)
  ) {
    return dateTime.slice(date.length, dateTime.length - time.length);
  }
  return ' ';
};

/**
 * The moment format of a date with its time, as the locale's own `lll`
 * (`LLL` spoken) writes it, but with the time on the user's clock: `lll`'s
 * time is the locale's `LT`, `h:mm A` in English whatever the clock (FE #438).
 * The date follows the active moment locale, so English follows the user's
 * date order (`setLocale`, FE #430): "Oct 24, 2026 8:49 PM",
 * "24 Oct 2026 20:49", "24 oct. 2026 20:49", "24. okt. 2026 kl. 20:49".
 */
export const dateTimeFormat = (
  dateTimeConfig?: DateTimeConfig,
  {style = 'visual'}: {style?: RangeStyle} = {},
) =>
  `${style === 'spoken' ? 'LL' : 'll'}${dateTimeJoiner(moment.locale(), style)}${clockFormat(dateTimeConfig)}`;

/**
 * Formats `[start, end]` (unix seconds) as a compact range that keeps the
 * time of each end, for the rolling windows ("previous 24 hours", #280),
 * in `timezone` and the active moment locale:
 *
 * - one day: the date once, then the two times, joined like the locale's
 *   one-month range ("Sep 24, 2026 12:00 AM – 11:59 PM");
 * - one year: the year once, where `RANGE_FORMATS` puts it for the locale
 *   ("Sep 23 10:00 AM – Sep 24, 2026 9:59 AM"). The month is not collapsed
 *   across two days, because each date carries its own time;
 * - two years: `lll – lll`.
 *
 * Across two days, a locale's bare en dash is widened with thin spaces.
 *
 * Each date sits next to its time the way the locale's own `lll` joins them,
 * the time on the user's clock when `dateTimeConfig` is given.
 *
 * Not react-intl's date-time range helper, which needs
 * the Intl range formatter Hermes lacks (`noFormatRange.test.ts`).
 */
export const formatDateRangeWithTime = (
  start: number,
  end: number,
  timezone: string,
  {
    style = 'visual',
    joinSpokenRange,
    dateTimeConfig,
  }: RangeWithTimeOptions = {},
) => {
  const from = moment.tz(start * 1000, timezone);
  const to = moment.tz(end * 1000, timezone);
  const formats = RANGE_FORMATS[from.locale()] ?? FALLBACK;
  const spoken = style === 'spoken';
  const cases = spoken ? spokenCases(formats) : formats;
  let dateTime = spoken ? 'LLL' : 'lll';
  let time = 'LT';
  if (dateTimeConfig) {
    dateTime = dateTimeFormat(dateTimeConfig, {style});
    time = clockFormat(dateTimeConfig);
  }

  const sameDay =
    from.year() === to.year() &&
    from.month() === to.month() &&
    from.date() === to.date();

  let startFormat = dateTime;
  let endFormat = dateTime;
  let separator = cases.crossYear[2];
  if (sameDay) {
    endFormat = time;
    separator = cases.sameMonth[2];
  } else if (from.year() === to.year()) {
    const joiner = dateTimeJoiner(from.locale(), style);
    const [startDate, endDate, yearSeparator] = cases.sameYear;
    startFormat = `${startDate}${joiner}${time}`;
    endFormat = `${endDate}${joiner}${time}`;
    separator = yearSeparator;
  }
  // Between a time and a date, a bare dash reads as a time range
  // ("10:00–24 wrz"): widen it with thin spaces, as CLDR spaces it too.
  if (!sameDay && separator === TIGHT) {
    separator = THIN;
  }
  const startText = from.format(startFormat);
  const endText = to.format(endFormat);

  if (spoken && joinSpokenRange) {
    return joinSpokenRange(startText, endText);
  }
  return `${startText}${separator}${endText}`;
};
